import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { BookOpen, MapPin, PlusCircle, LayoutDashboard, BookHeart, LogIn, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { cn } from "@/lib/utils";

function Item({ to, icon: Icon, label, active, testid, primary, badge = 0 }) {
  const Badge = badge > 0 ? (
    <span data-testid={`${testid}-badge`} className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-background">
      {badge > 9 ? "9+" : badge}
    </span>
  ) : null;

  if (primary) {
    return (
      <Link to={to} data-testid={testid} className="flex flex-col items-center justify-center flex-1 min-w-0 -mt-5">
        <span className="relative w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 bg-primary text-primary-foreground">
          <Icon className="w-6 h-6" />{Badge}
        </span>
        <span className="text-[10px] font-medium mt-1 text-foreground truncate max-w-full">{label}</span>
      </Link>
    );
  }
  return (
    <Link to={to} data-testid={testid}
      className={cn("relative flex flex-col items-center justify-center gap-1 flex-1 min-w-0 h-full transition-colors",
        active ? "text-primary" : "text-muted-foreground")}>
      <span className="relative"><Icon className={cn("w-5 h-5", active && "fill-primary/10")} />{Badge}</span>
      <span className="text-[10px] font-medium truncate max-w-full">{label}</span>
    </Link>
  );
}

export default function BottomNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);
  const is = (p) => (p === "/" ? pathname === "/" : pathname.startsWith(p));

  const loadUnread = useCallback(async () => {
    if (!user) { setUnread(0); return; }
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.filter((n) => !n.read && (n.type === "zona" || n.type === "wishlist")).length);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    loadUnread();
    if (!user) return;
    const t = setInterval(loadUnread, 15000);
    return () => clearInterval(t);
  }, [loadUnread, user, pathname]);

  return (
    <nav
      data-testid="bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur-md border-t border-border no-print"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="h-16 flex items-stretch justify-around px-1">
        <Item to="/" icon={BookOpen} label="Catalogo" active={is("/")} testid="bottomnav-catalogo" />
        <Item to="/mappa" icon={MapPin} label="Mappa" active={is("/mappa")} testid="bottomnav-mappa" />
        {user ? (
          <>
            <Item to="/pubblica" icon={PlusCircle} label="Pubblica" active={is("/pubblica")} testid="bottomnav-pubblica" primary />
            <Item to="/avvisi" icon={Bell} label="Avvisi" active={is("/avvisi")} testid="bottomnav-avvisi" badge={unread} />
            <Item to="/area-personale" icon={LayoutDashboard} label="Area" active={is("/area-personale")} testid="bottomnav-area" />
          </>
        ) : (
          <>
            <Item to="/dona" icon={BookHeart} label="Dona" active={is("/dona")} testid="bottomnav-dona" />
            <Item to="/accedi" icon={LogIn} label="Accedi" active={is("/accedi")} testid="bottomnav-accedi" primary />
          </>
        )}
      </div>
    </nav>
  );
}
